import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { gapErrorCode, type GapItem } from "@/api/project-gaps";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDeleteGap, useProjectGaps } from "@/hooks/useProjectGaps";
import { formatDateTime } from "@/lib/datetime";
import { cn } from "@/lib/utils";

import { GapFormDialog } from "./GapFormDialog";
import {
  ACCEPTANCE_LABELS,
  IMPACT_LABELS,
  ORIGIN_LABELS,
  WORKAROUND_TYPE_LABELS,
} from "./gapLabels";

/** O formulário aberto: GAP novo, ou o GAP em edição. */
type FormState = { gap?: GapItem } | null;

/**
 * Aba "Registro de GAPs" do nó 04 Fit-to-Standard (EF-FUNC-011, RF-03), depois
 * do Catálogo: o consultor faz a curadoria dos scope items e em seguida registra
 * o que o Public não atende. Totais, lista, "+ Novo GAP", edição e exclusão.
 *
 * Continua editável com o escopo aprovado: o aceite do cliente vem depois de o
 * Diagnóstico ser apresentado. O Diagnóstico só lista os GAPs, pelo índice.
 */
export function GapRegisterView({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useProjectGaps(projectId);
  const remove = useDeleteGap(projectId);
  const [form, setForm] = useState<FormState>(null);
  const [toDelete, setToDelete] = useState<GapItem | null>(null);
  const [deleteError, setDeleteError] = useState("");

  if (isLoading) {
    return <div className="text-xs text-muted-foreground">{t("Loading the GAP register...")}</div>;
  }
  if (error) {
    return (
      <div className="text-xs text-destructive">
        {gapErrorCode(error) === "forbidden"
          ? t("Only consultants can see the GAP register.")
          : t("Could not load the GAP register.")}
      </div>
    );
  }

  const totals = data?.totals ?? { gaps: 0, essential: 0, accepted: 0, pending_acceptance: 0 };
  const items = data?.items ?? [];

  async function confirmDelete() {
    if (!toDelete) return;
    setDeleteError("");
    try {
      await remove.mutateAsync(toDelete.id);
      setToDelete(null);
    } catch {
      setDeleteError(t("Could not delete the GAP. Try again."));
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold">{t("GAP register")}</div>
          <p className="text-xs text-muted-foreground">
            {t(
              "Every need that neither the standard nor an extension covers goes here, with its workaround.",
            )}
          </p>
        </div>
        <Button size="sm" onClick={() => setForm({})}>
          {t("+ New GAP")}
        </Button>
      </div>

      <ul aria-label={t("GAP totals")} className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {(
          [
            ["GAPs", totals.gaps],
            ["Essential GAPs", totals.essential],
            ["Accepted by the client", totals.accepted],
            ["Pending acceptance", totals.pending_acceptance],
          ] as const
        ).map(([label, value]) => (
          <li key={label} className="rounded-md border bg-white px-3 py-2">
            <span className="text-[10px] font-semibold uppercase text-muted-foreground">
              {t(label)}
            </span>
            <div className="font-mono text-xl font-bold">{value}</div>
          </li>
        ))}
      </ul>

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
          {t("No GAP recorded yet.")}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">{t("ID")}</TableHead>
                <TableHead className="text-xs">{t("Origin")}</TableHead>
                <TableHead className="text-xs">{t("Reference")}</TableHead>
                <TableHead className="text-xs">{t("Unmet need")}</TableHead>
                <TableHead className="text-xs">{t("Workaround type")}</TableHead>
                <TableHead className="text-xs">{t("Workaround")}</TableHead>
                <TableHead className="text-xs">{t("Impact")}</TableHead>
                <TableHead className="text-xs">{t("Essential")}</TableHead>
                <TableHead className="text-xs">{t("Owner")}</TableHead>
                <TableHead className="text-xs">{t("Acceptance")}</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((gap) => (
                <TableRow key={gap.id}>
                  <TableCell className="font-mono text-xs font-semibold">{gap.code}</TableCell>
                  <TableCell className="text-xs">{t(ORIGIN_LABELS[gap.origin_type])}</TableCell>
                  <TableCell
                    className={cn("text-xs", gap.origin_type === "wricef" && "font-mono")}
                  >
                    {gap.reference}
                  </TableCell>
                  <TableCell className="text-xs">{gap.need}</TableCell>
                  <TableCell className="text-xs">
                    {t(WORKAROUND_TYPE_LABELS[gap.workaround_type])}
                  </TableCell>
                  <TableCell className="max-w-[260px] truncate text-xs" title={gap.workaround}>
                    {gap.workaround}
                  </TableCell>
                  <TableCell className="text-xs">{t(IMPACT_LABELS[gap.impact])}</TableCell>
                  <TableCell className="text-xs">
                    {gap.essential ? (
                      <span className="rounded bg-red-50 px-1.5 py-0.5 font-semibold text-red-700">
                        {t("Yes")}
                      </span>
                    ) : (
                      t("No")
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {gap.owner ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    <span
                      className={cn(
                        "rounded px-1.5 py-0.5 font-semibold",
                        gap.acceptance === "accepted"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-amber-50 text-amber-700",
                      )}
                    >
                      {t(ACCEPTANCE_LABELS[gap.acceptance])}
                    </span>
                    <AcceptanceStamp gap={gap} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      aria-label={t("Edit {{code}}", { code: gap.code })}
                      onClick={() => setForm({ gap })}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      aria-label={t("Delete {{code}}", { code: gap.code })}
                      onClick={() => {
                        setDeleteError("");
                        setToDelete(gap);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <GapFormDialog
        open={form !== null}
        onOpenChange={(open) => !open && setForm(null)}
        projectId={projectId}
        gap={form?.gap}
      />

      <AlertDialog open={toDelete !== null} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("Delete {{code}}?", { code: toDelete?.code ?? "" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.wricef_obj_name
                ? t(
                    "If this is the last GAP of {{object}}, its WRICEF decision loses the \"Lack of fit\" reason and goes back to undecided.",
                    { object: toDelete.wricef_obj_name },
                  )
                : t("The GAP leaves the register and the adherence index. This cannot be undone.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError ? <p className="text-sm text-destructive">{deleteError}</p> : null}
          <AlertDialogFooter>
            <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              onClick={(event) => {
                // O diálogo fecha sozinho no clique; fica aberto até a exclusão voltar.
                event.preventDefault();
                void confirmDelete();
              }}
            >
              {t("Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Quem levou o aceite para "Em discussão" ou "Aceito", e quando, abaixo do selo
 * (EF-FUNC-011, RF-03: aceite "com usuário e data"). Só a data quando o nome não
 * veio; nada no pendente. Só aqui, que é interno: o drill-down do Diagnóstico
 * mostra só o selo do aceite (David, 30/10/2026).
 */
function AcceptanceStamp({ gap }: { gap: GapItem }) {
  const { t } = useTranslation();
  if (gap.acceptance === "pending" || !gap.acceptance_at) return null;

  const date = formatDateTime(gap.acceptance_at);
  const name = gap.acceptance_by_name;
  const text =
    gap.acceptance === "accepted"
      ? name
        ? t("Accepted by {{name}} on {{date}}", { name, date })
        : t("Accepted on {{date}}", { date })
      : name
        ? t("Under discussion since {{date}}, by {{name}}", { name, date })
        : t("Under discussion since {{date}}", { date });
  return <div className="mt-1 whitespace-nowrap text-[11px] text-muted-foreground">{text}</div>;
}
